import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosClientSignIn } from "../axios/api.js";

// One entry per fixed slot (Aadhaar Card, PAN Card, ...) — filled in if uploaded, empty otherwise.
export const fetchMyProfileDocuments = createAsyncThunk(
  "profileDocuments/fetchMine",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get("/profile-documents/mine");
      return response.data.slots || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load documents.");
    }
  }
);

export const uploadProfileDocument = createAsyncThunk(
  "profileDocuments/upload",
  async ({ documentType, file }, { rejectWithValue, dispatch }) => {
    try {
      const formData = new FormData();
      formData.append("documentType", documentType);
      formData.append("file", file);

      const response = await axiosClientSignIn.post("/profile-documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      dispatch(fetchMyProfileDocuments()); // refresh so the slot shows as filled
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Upload failed.");
    }
  }
);

export const deleteProfileDocument = createAsyncThunk(
  "profileDocuments/delete",
  async (documentId, { rejectWithValue, dispatch }) => {
    try {
      await axiosClientSignIn.delete(`/profile-documents/${documentId}`);
      dispatch(fetchMyProfileDocuments()); // refresh so the slot shows as empty again
      return documentId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Delete failed.");
    }
  }
);

// Returns a signed download URL — caller opens it, doesn't route through Redux state.
export const getProfileDocumentDownloadUrl = createAsyncThunk(
  "profileDocuments/getDownloadUrl",
  async (documentId, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get(`/profile-documents/download/${documentId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to open document.");
    }
  }
);

const profileDocumentSlice = createSlice({
  name: "profileDocuments",
  initialState: {
    slots: [],
    slotsLoading: false,
    uploadingType: null, // which slot is currently uploading, so only that card shows a spinner
    deletingId: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyProfileDocuments.pending, (state) => {
        state.slotsLoading = true;
      })
      .addCase(fetchMyProfileDocuments.fulfilled, (state, action) => {
        state.slotsLoading = false;
        state.slots = action.payload;
      })
      .addCase(fetchMyProfileDocuments.rejected, (state) => {
        state.slotsLoading = false;
      })

      .addCase(uploadProfileDocument.pending, (state, action) => {
        state.uploadingType = action.meta.arg.documentType;
      })
      .addCase(uploadProfileDocument.fulfilled, (state) => {
        state.uploadingType = null;
      })
      .addCase(uploadProfileDocument.rejected, (state) => {
        state.uploadingType = null;
      })

      .addCase(deleteProfileDocument.pending, (state, action) => {
        state.deletingId = action.meta.arg;
      })
      .addCase(deleteProfileDocument.fulfilled, (state) => {
        state.deletingId = null;
      })
      .addCase(deleteProfileDocument.rejected, (state) => {
        state.deletingId = null;
      });
  },
});

export default profileDocumentSlice.reducer;
